import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer as MuiDrawer, AppBar as MuiAppBar, Toolbar, List, CssBaseline,
  Typography, Divider, IconButton, ListItem, ListItemButton, ListItemIcon,
  ListItemText, Stack, Button, Modal, Tooltip, Zoom, Avatar, useMediaQuery
} from '@mui/material';

// Icons
import MenuIcon from '@mui/icons-material/Menu';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ClassOutlinedIcon from '@mui/icons-material/ClassOutlined';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PermContactCalendarOutlinedIcon from '@mui/icons-material/PermContactCalendarOutlined';

import { useAuth } from '../authentication/auth';

const drawerWidth = 240; 
const collapsedWidth = 88;

const openedMixin = (theme) => ({
  width: drawerWidth,
  backgroundColor: '#F1F2F6',
  borderRight: 'none',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  backgroundColor: '#F1F2F6',
  borderRight: 'none',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: collapsedWidth,
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

const Sidebar = () => {
  const auth = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const open = !isTablet; 
  
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [logoutModal, setLogoutModal] = React.useState(false);

  const userRole = localStorage.getItem("new_auth") || "Tutor";

  const SidebarSections = [
    {
      label: "MAIN MENU",
      items: [
        { title: "Dashboard", icon: <TableChartOutlinedIcon />, link: "/Portal", access: ["IT", "Admin", "Tutor"] },
        { title: "Staff", icon: <SchoolOutlinedIcon />, link: "/Portal/Staff", access: ["IT", "Admin"] },
        { title: "Parent", icon: <Groups2OutlinedIcon />, link: "/Portal/Parent", access: ["IT", "Admin"] },
        { title: "Student", icon: <PeopleOutlinedIcon />, link: "/Portal/Students", access: ["IT", "Admin", "Tutor"] },
        { title: "Attendance", icon: <ChecklistIcon />, link: "/Portal/TutorAttendance", access: ["IT", "Admin"] },
        { title: "Invoice", icon: <ReceiptOutlinedIcon />, link: "/Portal/Invoice", access: ["IT", "Admin"] },
        { title: "Receipt", icon: <ReceiptLongOutlinedIcon />, link: "/Portal/Receipt", access: ["IT", "Admin"] },
        { title: "Class", icon: <ClassOutlinedIcon />, link: "/Portal/Class", access: ["IT", "Admin"] },
        { title: "Staff Leave", icon: <ExitToAppOutlinedIcon />, link: "/Portal/StaffLeave", access: ["IT", "Admin"] },
      ]
    },
    {
      label: "ACADEMIC",
      items: [
        { title: "Exam Old", icon: <DescriptionOutlinedIcon />, link: "/Portal/ExamTemp", access: ["IT", "Admin", "Tutor"] },
        { title: "Exam Preview", icon: <InsertChartOutlinedIcon />, link: "/Portal/ExamPreview", access: ["IT", "Admin", "Tutor"] },
        { title: "Exam", icon: <DescriptionOutlinedIcon />, link: "/Portal/Exam", access: ["IT", "Admin", "Tutor"] },
        { title: "Exam Slip", icon: <ListAltIcon />, link: "/Portal/ExamSlip", access: ["IT", "Admin"] },
        { title: "Exam Missing Student", icon: <Groups2OutlinedIcon />, link: "/Portal/ExamMissingStudent", access: ["IT", "Admin"] },
      ]
    },
    {
      label: "SYSTEM",
      items: [
        { title: "Announcement", icon: <CampaignOutlinedIcon />, link: "/Portal/Announcement", access: ["IT", "Admin"] },
        { title: "Batch", icon: <PermContactCalendarOutlinedIcon />, link: "/Portal/Batch", access: ["IT", "Admin"] },
        { title: "Settings", icon: <SettingsOutlinedIcon />, link: "/Portal/Settings", access: ["IT", "Admin", "Tutor"] },
      ]
    }
  ];

  const drawerContent = (isDrawerExpanded) => (
    <>
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: isDrawerExpanded ? 'flex-start' : 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ bgcolor: '#ff9800', p: 0.5, borderRadius: 1.5, display: 'flex' }}>
            <SchoolOutlinedIcon sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          {isDrawerExpanded && <Typography variant="h6" fontWeight="bold" color="#ff9800">Genius Milenia</Typography>}
        </Stack>
      </Box>

      <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flexGrow: 1, px: 1 }}>
        {SidebarSections.map((section) => (
          <React.Fragment key={section.label}>
            {isDrawerExpanded && (
              <Typography variant="caption" sx={{ px: 3, color: 'text.secondary', fontWeight: 'bold', display: 'block', mt: 2, mb: 1 }}>
                {section.label}
              </Typography>
            )}
            <List>
              {section.items.map((item) => {
                const isActive = item.link === "/Portal" 
                    ? location.pathname === "/Portal" 
                    : location.pathname.split("/")[2] === item.link.split("/")[2];

                if (!item.access.includes(userRole)) return null;

                return (
                  <ListItem key={item.title} disablePadding sx={{ mb: 0.5 }}>
                    <Tooltip title={!isDrawerExpanded ? item.title : ""} placement="right" arrow TransitionComponent={Zoom}>
                      <ListItemButton
                        onClick={() => { 
                           localStorage.setItem("current_tab", item.title);
                           navigate(item.link); 
                           if(isMobile) setMobileOpen(false); 
                        }}
                        sx={{
                          minHeight: 48,
                          justifyContent: isDrawerExpanded ? 'initial' : 'center',
                          px: 2.5,
                          borderRadius: '12px',
                          mx: isDrawerExpanded ? 1 : 0.5,
                          backgroundColor: isActive ? '#FFFFFF' : 'transparent',
                          boxShadow: isActive ? '0px 2px 8px rgba(0,0,0,0.05)' : 'none',
                          '&:hover': { backgroundColor: isActive ? '#FFFFFF' : '#E8EAED' },
                        }}
                      >
                        <ListItemIcon sx={{ 
                          minWidth: 0, 
                          mr: isDrawerExpanded ? 2 : 'auto', 
                          justifyContent: 'center', 
                          color: isActive ? '#ff9800' : '#5F6368' 
                        }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.title} 
                          sx={{ opacity: isDrawerExpanded ? 1 : 0 }} 
                          primaryTypographyProps={{ 
                            fontSize: '0.85rem', 
                            fontWeight: isActive ? 'bold' : 'medium', 
                            color: isActive ? '#ff9800' : 'inherit' 
                          }}
                        />
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                );
              })}
            </List>
          </React.Fragment>
        ))}
      </Box>

      <Divider sx={{ mx: 2, my: 1 }} />
      <Box sx={{ p: 2 }}>
        <ListItemButton onClick={() => setLogoutModal(true)} sx={{ borderRadius: '12px', justifyContent: isDrawerExpanded ? 'initial' : 'center' }}>
          <Avatar sx={{ width: 32, height: 32, mr: isDrawerExpanded ? 1.5 : 0, bgcolor: '#ff9800' }}>{userRole.charAt(0)}</Avatar>
          {isDrawerExpanded && <ListItemText primary="Log Out" secondary={userRole} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 'bold' }} secondaryTypographyProps={{ fontSize: '0.7rem' }} />}
        </ListItemButton>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: '#F8F9FA', minHeight: '100vh' }}>
      <CssBaseline />
      
      {isMobile && (
        <MuiAppBar position="fixed" elevation={0} sx={{ bgcolor: 'white', color: 'black', borderBottom: '1px solid #eee', zIndex: (theme) => theme.zIndex.drawer - 1 }}>
          <Toolbar>
            <IconButton onClick={() => setMobileOpen(true)} edge="start" sx={{ mr: 2 }}><MenuIcon /></IconButton>
            <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }}>Genius Milenia</Typography>
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#ff9800' }}>{userRole.charAt(0)}</Avatar>
          </Toolbar>
        </MuiAppBar>
      )}

      <Box component="nav">
        {isMobile ? (
          <MuiDrawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', backgroundColor: '#F1F2F6', borderRight: 'none' } }}
          >
            {drawerContent(true)}
          </MuiDrawer>
        ) : (
          <Drawer variant="permanent" open={open}>
            {drawerContent(open)}
          </Drawer>
        )}
      </Box>

      <Box component="main" sx={{ flexGrow: 1, mt: isMobile ? 8 : 0 }}>
        <Outlet />
      </Box>

      <Modal open={logoutModal} onClose={() => setLogoutModal(false)}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 300, bgcolor: 'background.paper', borderRadius: 4, p: 4, textAlign: 'center' }}>
          <Typography variant="h6" fontWeight="bold">Log Out</Typography>
          <Typography variant="body2" sx={{ my: 2 }}>Confirm log out this account?</Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button onClick={() => setLogoutModal(false)}>Cancel</Button>
            <Button 
                onClick={() => {
                    auth.logout();
                    localStorage.removeItem("current_tab");
                }} 
                component={Link} 
                to="/Portal/Login" 
                variant="contained" 
                sx={{ bgcolor: "#C62828", color: "white" }}
            >
                Log Out
            </Button>
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
};

export default Sidebar;